import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import initializeFirestore from '../utils/initFirestore';
import { uploadAllPlaceholders } from '../utils/uploadPlaceholders';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Button, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { 
  Storage as StorageIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Category as CategoryIcon,
  Shop as ShopIcon,
  Dataset as DatasetIcon,
  AccountCircle as AccountCircleIcon,
  Check as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

const InitDatabase = () => {
  const { currentUser, isAdmin, loading: authLoading } = useAuth();
  const [initializing, setInitializing] = useState(false);
  const [uploadingPlaceholders, setUploadingPlaceholders] = useState(false);
  const [initComplete, setInitComplete] = useState(false);
  const [placeholdersUploaded, setPlaceholdersUploaded] = useState(false);
  const [initError, setInitError] = useState(null);
  const [placeholdersError, setPlaceholdersError] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [adminCredentials, setAdminCredentials] = useState(null);
  
  // 如果用户无管理员权限，重定向到首页
  if (!authLoading && (!currentUser || !isAdmin)) {
    return <Navigate to="/login" replace />;
  }
  
  // 处理初始化数据库
  const handleInitDatabase = async () => {
    try {
      setInitializing(true);
      setInitError(null);
      
      // 调用初始化函数
      const result = await initializeFirestore();
      
      if (result.success) {
        setInitComplete(true);
        setNotification({
          open: true,
          message: '数据库初始化成功',
          severity: 'success'
        });
        
        // 保存管理员凭据，如果有
        if (result.adminEmail && result.adminPassword) {
          setAdminCredentials({
            email: result.adminEmail,
            password: result.adminPassword
          });
        }
      } else {
        setInitError(result.message || '初始化失败');
        setNotification({
          open: true,
          message: '数据库初始化失败',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('数据库初始化失败:', error);
      setInitError(error.message);
      setNotification({
        open: true,
        message: '数据库初始化失败: ' + error.message,
        severity: 'error'
      });
    } finally {
      setInitializing(false);
    }
  };
  
  // 处理上传占位图到Firebase Storage
  const handleUploadPlaceholders = async () => {
    try {
      setUploadingPlaceholders(true);
      setPlaceholdersError(null);
      
      // 调用上传函数
      const result = await uploadAllPlaceholders();
      
      setPlaceholdersUploaded(true);
      setNotification({
        open: true,
        message: '占位图上传成功',
        severity: 'success'
      });
      
      console.log('占位图上传成功:', result);
    } catch (error) {
      console.error('占位图上传失败:', error);
      setPlaceholdersError(error.message);
      setNotification({
        open: true,
        message: '占位图上传失败: ' + error.message,
        severity: 'error'
      });
    } finally {
      setUploadingPlaceholders(false);
    }
  };

  // 关闭通知
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };
  
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          初始化数据库
        </Typography>
        
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          此页面将初始化Firestore数据库，创建必要的集合和基础数据。
        </Typography>
        
        <Alert severity="warning" sx={{ my: 2 }}>
          注意：初始化操作将在数据库中创建示例数据。此操作不会覆盖现有数据，但建议在空数据库上执行。
        </Alert>
        
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            将创建以下数据集合：
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <CategoryIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="产品分类 (categories)" 
                secondary="基础产品分类体系，包括水晶手链、项链等5个分类" 
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <ShopIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="产品 (products)" 
                secondary="初始产品示例，包括不同类型的水晶产品" 
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <PersonIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="命理师 (fortuneTellers)" 
                secondary="初始命理师数据，包括个人简介、专长和预约信息" 
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <AccountCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="用户 (users)" 
                secondary="创建系统管理员账户" 
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <SettingsIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="系统设置 (settings)" 
                secondary="基础网站设置，包括联系信息、配送选项等" 
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <StorageIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="占位图 (placeholders)" 
                secondary="上传用户头像和产品图片占位图，解决CORS和CSP问题" 
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <DatasetIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="评价集合 (comments)" 
                secondary="产品和命理师的评价系统，包括评分、内容和管理员回复" 
              />
            </ListItem>
          </List>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {initComplete ? (
          <Box sx={{ mt: 3 }}>
            <Alert icon={<CheckIcon />} severity="success" sx={{ mb: 3 }}>
              数据库初始化完成!
            </Alert>
            
            {adminCredentials && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom>
                  管理员账户信息
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  邮箱: {adminCredentials.email}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  密码: {adminCredentials.password}
                </Typography>
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  重要：请记录此密码信息，并在首次登录后修改密码!
                </Typography>
              </Paper>
            )}
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => window.location.href = '/'}
              >
                返回首页
              </Button>
              
              <Button 
                variant="outlined"
                onClick={() => window.location.reload()}
              >
                刷新页面
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 3 }}>
            {initError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {initError}
              </Alert>
            )}
            
            {placeholdersError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                占位图上传错误: {placeholdersError}
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                color="primary" 
                size="large"
                onClick={handleInitDatabase}
                disabled={initializing}
                startIcon={initializing ? <CircularProgress size={20} /> : <DatasetIcon />}
              >
                {initializing ? '初始化中...' : '开始初始化数据库'}
              </Button>
              
              <Button 
                variant="outlined" 
                color="secondary" 
                size="large"
                onClick={handleUploadPlaceholders}
                disabled={uploadingPlaceholders}
                startIcon={uploadingPlaceholders ? <CircularProgress size={20} /> : <StorageIcon />}
              >
                {uploadingPlaceholders ? '上传中...' : '上传占位图'}
              </Button>
              
              {placeholdersUploaded && (
                <Alert icon={<CheckIcon />} severity="success" sx={{ mt: 2, width: '100%' }}>
                  占位图上传成功!
                </Alert>
              )}
            </Box>
          </Box>
        )}
      </Paper>
      
      {/* 通知消息 */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default InitDatabase;